/*  iceberg-ui  */
import { Box, Flex } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { ContentCopy } from '@mui/icons-material';
import { Grid, Typography } from '@mui/material';

/* Shared  */
import { Notify } from 'application/Shared/Helpers';

/*  Helpers */
import { Tenant } from 'application/Shared/types/tenant';
import { format } from 'date-fns';

/*  React  */
import React from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ClientsTable } from './ClientsTable';
import { CustomCard } from './Shared/CustomCard';
import { TenantStats } from './TenantStats';
import { tenantHasSupportAccess } from './functions';

const PartnerDetails = ({ partnerTenant }: { partnerTenant?: Tenant }) =>
    partnerTenant ? (
        <TenantInfo
            tenant={partnerTenant}
            title="Partner Tenant Details"
            linkToTenant
        />
    ) : null;

const formatDate = (date: Date | string) =>
    format(new Date(date), 'MMMM d, yyyy @ hh:mm a zzz');

const TenantDetailItem = ({
    label,
    value,
    showCopyIcon,
    link
}: {
    label: string;
    value: string;
    showCopyIcon?: boolean;
    link?: string;
}) => (
    <Grid item xs={12} md={6}>
        <Box>
            <Typography color="textSecondary" fontWeight={600}>
                {label}
            </Typography>
            <Flex alignItems="center">
                {link ? <Link to={link}>{value}</Link> : value}
                {showCopyIcon && (
                    <CopyToClipboard
                        text={value}
                        onCopy={() => {
                            Notify.s('ID copied to clipboard');
                        }}
                    >
                        <Box sx={{ cursor: 'pointer', marginLeft: '16px' }}>
                            <ContentCopy />
                        </Box>
                    </CopyToClipboard>
                )}
            </Flex>
        </Box>
    </Grid>
);

const TenantInfo = ({
    tenant,
    title = 'Tenant Details',
    linkToTenant = false
}: {
    tenant: Tenant;
    title?: string;
    linkToTenant?: boolean;
}) => {
    const { t } = useTranslation();
    return (
        <CustomCard>
            <Box mb={2}>
                <Typography variant="h4">{title}</Typography>
            </Box>
            <Grid container spacing={3}>
                <TenantDetailItem
                    label={t('GlobalAdmin.Tenant.Details.Name.label')}
                    value={tenant.name}
                    link={
                        linkToTenant
                            ? `/globaladmin/tenant/${tenant.id}`
                            : undefined
                    }
                />
                <TenantDetailItem
                    label={t('GlobalAdmin.Tenant.Details.ID.label')}
                    value={tenant.id}
                    showCopyIcon
                />
                <TenantDetailItem
                    label={t('GlobalAdmin.Tenant.Details.Created.label')}
                    value={formatDate(tenant.dateCreated)}
                />
                <TenantDetailItem
                    label={t(
                        'GlobalAdmin.Tenant.Details.PartnerOrClient.label'
                    )}
                    value={tenant.partner ? 'Partner' : 'Client'}
                />
                <TenantDetailItem
                    label={t('GlobalAdmin.Tenant.Details.Region.label')}
                    value={tenant.region}
                />

                {tenant.partner && (
                    <>
                        {tenant.identityTenantDomain && (
                            <TenantDetailItem
                                label={t(
                                    'GlobalAdmin.Tenant.Details.IdentityTenantDomain.label'
                                )}
                                value={tenant.identityTenantDomain}
                            />
                        )}
                        {tenant.identityTenantType && (
                            <TenantDetailItem
                                label={t(
                                    'GlobalAdmin.Tenant.Details.IdentityTenantType.label'
                                )}
                                value={tenant.identityTenantType}
                            />
                        )}
                        {tenant.identityTenantId && (
                            <TenantDetailItem
                                label={t(
                                    'GlobalAdmin.Tenant.Details.IdentityTenantId.label'
                                )}
                                value={tenant.identityTenantId}
                            />
                        )}
                    </>
                )}
                {!tenant.partner && (
                    <TenantDetailItem
                        label={t('GlobalAdmin.Tenant.Details.Domains.label')}
                        value={tenant.domains.join(', ')}
                    />
                )}
            </Grid>
        </CustomCard>
    );
};

const TenantSettings = ({ tenant }: { tenant: Tenant }) => {
    const { t } = useTranslation();

    return (
        <CustomCard>
            <Box mb={2}>
                <Typography variant="h4">
                    {t('GlobalAdmin.Tenant.Details.TenantSettings.title')}
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {tenant.partner && (
                    <>
                        <TenantDetailItem
                            label={t(
                                'GlobalAdmin.Tenant.Details.HubType.label'
                            )}
                            value={
                                tenant.settings?.hubType ||
                                t('GlobalAdmin.Tenant.Details.HubType.None')
                            }
                        />
                        <TenantDetailItem
                            label={t(
                                'GlobalAdmin.Tenant.Details.TrialEndDate.label'
                            )}
                            value={
                                tenant.settings?.trialEndDate
                                    ? formatDate(tenant.settings?.trialEndDate)
                                    : t(
                                          'GlobalAdmin.Tenant.Details.TrialEndDate.NotSet'
                                      )
                            }
                        />
                        <TenantDetailItem
                            label={t(
                                'GlobalAdmin.Tenant.Details.SupportAccessEnabled'
                            )}
                            value={
                                tenantHasSupportAccess(tenant)
                                    ? t('Common.yes')
                                    : t('Common.no')
                            }
                        />
                    </>
                )}
            </Grid>
        </CustomCard>
    );
};

export const TenantDetails = ({
    tenant,
    partnerTenant
}: {
    tenant: Tenant;
    partnerTenant: Tenant;
}) => {
    const { t } = useTranslation();
    return (
        <CustomCard>
            <TenantInfo tenant={tenant} />
            {tenant.partner &&
                tenant.settings &&
                Object.keys(tenant.settings).length > 0 && (
                    <TenantSettings tenant={tenant} />
                )}

            <PartnerDetails partnerTenant={partnerTenant} />

            {tenant.partner && <ClientsTable tenant={tenant} />}

            {tenant.partner && (
                <TenantStats
                    tenant={tenant}
                    statType="billingUsers"
                    title={t(
                        'GlobalAdmin.Tenant.Details.TenantStats.BillingUsers.title'
                    )}
                    description={
                        <>
                            <Typography
                                variant="body1"
                                marginBottom="8px"
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        'GlobalAdmin.Tenant.Details.TenantStats.description.partOne',
                                        {
                                            tenantName: tenant.name
                                        }
                                    )
                                }}
                            />

                            <Typography variant="body1">
                                {t(
                                    'GlobalAdmin.Tenant.Details.TenantStats.description.partTwo'
                                )}
                            </Typography>
                        </>
                    }
                />
            )}

            {!tenant.partner && (
                <TenantStats
                    tenant={tenant}
                    statType="totalUsers"
                    title={t(
                        'GlobalAdmin.Tenant.Details.TenantStats.TotalUsers.title'
                    )}
                    description={
                        <Typography
                            variant="body1"
                            marginBottom="8px"
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    'GlobalAdmin.Tenant.Details.TenantStats.totalUsers.description',
                                    {
                                        tenantName: tenant.name
                                    }
                                )
                            }}
                        />
                    }
                />
            )}
        </CustomCard>
    );
};
